import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import MediaGallery from '../components/MediaGallery';
import VoiceSelector from '../components/VoiceSelector';
import VideoMixPlayer from '../components/VideoMixPlayer';
import { VolumeControl, AudioMixPanel, getStoredVolume } from '../components/VolumeControls';
import { 
  Sparkles, Loader2, Download, Wand2, Play, Upload,
  ChevronDown, ChevronUp, Lightbulb, Video, Layers,
  Film, Mic, Music, Subtitles, RefreshCw, Check, X,
  Zap, Target, TrendingUp, Clapperboard, Megaphone, GraduationCap,
  Clock, Smartphone, Youtube, Instagram, Facebook
} from 'lucide-react';
import { toast } from 'sonner';
import AppShell from '../components/AppShell';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Video inspiration examples
const INSPIRATION_EXAMPLES = [
  {
    prompt: "5 morning habits that changed my life - motivational fitness content with energetic transitions",
    category: "Motivational",
    icon: Zap,
    color: "from-orange-500 to-red-600"
  },
  {
    prompt: "Introducing our new wireless earbuds - sleek product showcase with cinematic lighting",
    category: "Product Promo",
    icon: Megaphone,
    color: "from-blue-500 to-indigo-600"
  },
  {
    prompt: "3 things you didn't know about saving money - quick tips with bold captions",
    category: "Educational",
    icon: GraduationCap,
    color: "from-emerald-500 to-teal-600"
  },
  {
    prompt: "A day in my life as a content creator - aesthetic vlog style with smooth transitions",
    category: "Storytelling",
    icon: Clapperboard,
    color: "from-purple-500 to-pink-600"
  },
  {
    prompt: "POV: You finally started that side hustle - viral hook with relatable content",
    category: "Viral Hook",
    icon: TrendingUp,
    color: "from-amber-500 to-orange-600"
  },
  {
    prompt: "Why most people fail at productivity - YouTube Shorts style with engagement hooks",
    category: "YouTube Shorts",
    icon: Youtube,
    color: "from-red-500 to-rose-600"
  }
];

// Friendly generation messages for videos
const GENERATION_MESSAGES = [
  "Planning your scenes...",
  "Creating visuals...",
  "Adding voice & music...",
  "Rendering your video...",
  "Finalizing..."
];

// Style options
const STYLE_OPTIONS = [
  { value: 'cinematic', label: 'Cinematic', desc: 'Film-like quality' },
  { value: 'modern', label: 'Social Media', desc: 'Trending style' },
  { value: 'documentary', label: 'Documentary', desc: 'Professional narration' },
  { value: 'animated', label: 'Animated', desc: 'Motion graphics' },
  { value: 'commercial', label: 'Ad Creative', desc: 'Conversion-focused' },
  { value: 'viral', label: 'Viral/Trendy', desc: 'Engagement optimized' }
];

// Video player with real-time audio mix controls
const VideoPlayerWithControls = ({ src, poster, videoId, token, onExportMix, userTier, isFounder }) => {
  return (
    <VideoMixPlayer
      videoId={videoId}
      videoSrc={src}
      poster={poster}
      token={token}
      onExportMix={onExportMix}
      userTier={userTier}
      isFounder={isFounder}
    />
  );
};

// Platform options
const PLATFORM_OPTIONS = [
  { value: 'instagram', label: 'Instagram Reels', aspect: '9:16', icon: Instagram },
  { value: 'tiktok', label: 'TikTok', aspect: '9:16', icon: Smartphone },
  { value: 'youtube_shorts', label: 'YouTube Shorts', aspect: '9:16', icon: Youtube },
  { value: 'youtube', label: 'YouTube Landscape', aspect: '16:9', icon: Youtube },
  { value: 'facebook', label: 'Facebook', aspect: '1:1 / 4:5', icon: Facebook }
];

// Duration options
const DURATION_OPTIONS = [
  { value: '10', label: '5-10s', desc: 'Quick hook' },
  { value: '20', label: '10-20s', desc: 'Short clip' },
  { value: '30', label: '20-30s', desc: 'Standard reel' },
  { value: '60', label: '30-60s', desc: 'Extended' },
  { value: '180', label: 'Long-form', desc: 'YouTube style' }
];

// Voice options removed - now using VoiceSelector component with dynamic voices from API

const VideosStudioPage = () => {
  const { token, user } = useAuth();

  // State
  const [activeTab, setActiveTab] = useState('create');
  const [prompt, setPrompt] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentMessage, setCurrentMessage] = useState(0);
  
  // Video settings
  const [style, setStyle] = useState('modern');
  const [platform, setPlatform] = useState('instagram');
  const [duration, setDuration] = useState('30');
  const [voice, setVoice] = useState('warm_narrator');
  const [voiceMode, setVoiceMode] = useState('standard');
  const [captions, setCaptions] = useState(true);
  const [voiceover, setVoiceover] = useState(true);
  const [backgroundMusic, setBackgroundMusic] = useState(true);
  const [motionPreset, setMotionPreset] = useState('none');
  
  // Jobs and videos
  const [jobs, setJobs] = useState([]);
  const [videos, setVideos] = useState([]);
  const [latestResult, setLatestResult] = useState(null);
  
  const pollIntervalRef = useRef(null);

  // Fetch videos
  const fetchVideos = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/reels/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVideos(response.data || []);
    } catch (error) {
      console.error('Failed to fetch videos:', error);
    }
  }, [token]);

  // Fetch jobs queue
  const fetchJobs = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/jobs/queue`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const newJobs = response.data.jobs || [];
      setJobs(newJobs);
      
      // Check if any job just completed
      const completedJob = newJobs.find(j => 
        j.status === 'ready' && j.video_url && !latestResult
      );
      if (completedJob) {
        setLatestResult(completedJob);
        toast.success('Your video is ready!');
      }
      
      return newJobs;
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
      return [];
    }
  }, [token, latestResult]);

  useEffect(() => {
    fetchVideos();
    fetchJobs();
    
    // Poll for job updates
    pollIntervalRef.current = setInterval(fetchJobs, 3000);
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [fetchVideos, fetchJobs]);

  // Cycle through generation messages
  useEffect(() => {
    if (generating) {
      const interval = setInterval(() => {
        setCurrentMessage(prev => (prev + 1) % GENERATION_MESSAGES.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [generating]);

  // Simulate progress based on job status
  useEffect(() => {
    if (generating && jobs.length > 0) {
      const currentJob = jobs[0];
      if (currentJob?.render_progress) {
        setProgress(currentJob.render_progress);
      }
    }
  }, [jobs, generating]);

  // Handle inspiration click
  const handleInspirationClick = (example) => {
    setPrompt(example.prompt);
    toast.success('Prompt loaded! Customize it or generate now.');
  };

  // Generate video
  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please describe the video you want to create');
      return;
    }

    setGenerating(true);
    setProgress(0);
    setLatestResult(null);

    try {
      const response = await axios.post(
        `${API}/reels/`,
        {
          prompt: prompt.trim(),
          platform: platform,
          category: 'general',
          voice: voiceover ? voice : null,
          voice_mode: voiceover ? voiceMode : null,
          language: 'en',
          motion_preset: motionPreset !== 'none' ? motionPreset : undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Video creation started! This may take a few minutes.');
      
      // Start polling more frequently
      const checkJob = async () => {
        const jobs = await fetchJobs();
        const job = jobs.find(j => j.id === response.data.id);
        
        if (job) {
          setProgress(job.render_progress || 0);
          
          if (job.status === 'ready' && job.video_url) {
            setLatestResult(job);
            setGenerating(false);
            fetchVideos();
            toast.success('Your video is ready!');
            return true;
          } else if (job.status === 'failed' || job.status === 'error') {
            setGenerating(false);
            toast.error('Video generation failed. Please try again.');
            return true;
          }
        }
        return false;
      };

      // Poll every 3 seconds
      const poll = setInterval(async () => {
        const done = await checkJob();
        if (done) {
          clearInterval(poll);
        }
      }, 3000);

      // Timeout after 5 minutes
      setTimeout(() => {
        clearInterval(poll);
        if (generating) {
          setGenerating(false);
          toast.info('Video is still processing. Check the Gallery for updates.');
        }
      }, 300000);

    } catch (error) {
      console.error('Generation failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to create video');
      setGenerating(false);
    }
  };

  // Handle upload
  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    try {
      toast.info('Uploading video...');
      await axios.post(`${API}/uploads/video`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      toast.success('Video uploaded!');
      fetchVideos();
      setActiveTab('gallery');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    }
  };

  // Active jobs (for progress display)
  const activeJobs = jobs.filter(j => 
    ['queued', 'generating', 'processing', 'rendering'].includes(j.status)
  );

  return (
    <AppShell>
      <div className="p-5 sm:p-7 lg:p-10 pb-20">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="relative max-w-5xl mx-auto">
          {/* Header */}
          <div className="mb-10">
            <p className="text-[10px] text-primary/60 uppercase tracking-[0.2em] font-semibold mb-3">Videos Engine</p>
            <h1 className="text-3xl sm:text-4xl font-bold mb-3 tracking-tight text-white">
              AI Video Studio
            </h1>
            <p className="text-zinc-500 text-base max-w-2xl">
              Describe your idea and let AI handle the visuals, voice, and music.
            </p>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="flex justify-center mb-8">
              <TabsList className="bg-zinc-900/80 border border-zinc-800 p-1 rounded-full">
                <TabsTrigger 
                  value="create" 
                  className="rounded-full px-6 py-2 data-[state=active]:bg-primary data-[state=active]:text-white"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Create
                </TabsTrigger>
                <TabsTrigger 
                  value="upload" 
                  className="rounded-full px-6 py-2 data-[state=active]:bg-primary data-[state=active]:text-white"
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload
                </TabsTrigger>
                <TabsTrigger 
                  value="gallery" 
                  className="rounded-full px-6 py-2 data-[state=active]:bg-primary data-[state=active]:text-white"
                >
                  <Layers className="w-4 h-4 mr-2" />
                  Gallery
                </TabsTrigger>
              </TabsList>
            </div>

            {/* Create Tab - Prompt First Experience */}
            <TabsContent value="create" className="space-y-8 animate-fade-in-up">
              {/* Main Prompt Area */}
              <div className="max-w-3xl mx-auto">
                {/* Prompt Input */}
                <div className="relative mb-6">
                  <div className="absolute -inset-1 bg-gradient-to-r from-primary/20 via-purple-500/10 to-primary/20 rounded-3xl blur-xl opacity-60" />
                  <div className="relative">
                    <Textarea
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                      placeholder="Create a motivational fitness reel with dynamic cuts and bold captions..."
                      className="prompt-input w-full px-6 py-5 text-lg"
                      disabled={generating}
                      rows={4}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                          handleGenerate();
                        }
                      }}
                    />
                    <div className="absolute bottom-4 right-4 text-xs text-zinc-500 hidden sm:block">
                      ⌘/Ctrl + Enter to generate
                    </div>
                  </div>
                </div>

                {/* Generate Button */}
                <div className="flex flex-col items-center gap-4 mb-8">
                  <Button
                    onClick={handleGenerate}
                    disabled={!prompt.trim() || generating}
                    className={`btn-generate min-w-[200px] ${generating ? 'btn-generating' : ''}`}
                    data-testid="generate-video-btn"
                  >
                    {generating ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        {GENERATION_MESSAGES[currentMessage]}
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-5 h-5 mr-2" />
                        Generate Video
                      </>
                    )}
                  </Button>

                  {/* Progress Bar */}
                  {generating && (
                    <div className="w-full max-w-md space-y-2 animate-fade-in-up">
                      <div className="progress-creative">
                        <div 
                          className="progress-creative-bar" 
                          style={{ width: `${Math.max(10, progress)}%` }}
                        />
                      </div>
                      <p className="text-center text-sm text-zinc-500">
                        {progress > 0 ? `${Math.round(progress)}% complete` : 'Starting...'}
                      </p>
                    </div>
                  )}
                </div>

                {/* Active Jobs Queue */}
                {activeJobs.length > 0 && !generating && (
                  <div className="mb-8 space-y-2">
                    {activeJobs.slice(0, 3).map((job) => (
                      <div 
                        key={job.id}
                        className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4 flex items-center gap-4"
                      >
                        <div className="w-10 h-10 bg-primary/20 rounded-lg flex items-center justify-center">
                          <Loader2 className="w-5 h-5 text-primary animate-spin" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{job.topic || job.prompt?.slice(0, 50)}</p>
                          <p className="text-xs text-zinc-500">{job.render_message || 'Processing...'}</p>
                        </div>
                        <div className="text-xs text-primary font-medium">
                          {job.render_progress ? `${Math.round(job.render_progress)}%` : 'Queued'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Advanced Settings (Collapsed by Default) */}
                <div className="collapsible-panel mb-8">
                  <button
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="collapsible-trigger"
                  >
                    <span className="flex items-center gap-2">
                      <Wand2 className="w-4 h-4" />
                      Customize Video Settings
                    </span>
                    {showAdvanced ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </button>
                  
                  {showAdvanced && (
                    <div className="collapsible-content animate-fade-in-up space-y-6">
                      {/* Video Style */}
                      <div className="space-y-3">
                        <Label className="text-zinc-400">Video Style</Label>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {STYLE_OPTIONS.map((s) => (
                            <button
                              key={s.value}
                              onClick={() => setStyle(s.value)}
                              className={`p-3 rounded-xl border text-left transition-all ${
                                style === s.value
                                  ? 'bg-primary/10 border-primary text-white'
                                  : 'bg-zinc-900/50 border-zinc-800 text-zinc-400 hover:border-zinc-700'
                              }`}
                            >
                              <p className="font-medium text-sm">{s.label}</p>
                              <p className="text-xs opacity-60">{s.desc}</p>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Motion Presets */}
                      <div className="space-y-3">
                        <Label className="text-zinc-400">Motion & Camera</Label>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                          {[
                            { value: 'none', label: 'None', desc: 'No effect' },
                            { value: 'zoom_in', label: 'Zoom In', desc: 'Slow dolly in' },
                            { value: 'zoom_out', label: 'Zoom Out', desc: 'Pull back reveal' },
                            { value: 'pan_left', label: 'Pan Left', desc: 'Smooth pan L' },
                            { value: 'pan_right', label: 'Pan Right', desc: 'Smooth pan R' },
                            { value: 'tilt_up', label: 'Tilt Up', desc: 'Dramatic rise' },
                            { value: 'orbit', label: 'Orbit', desc: 'Rotating shot' },
                            { value: 'dolly_zoom', label: 'Dolly Zoom', desc: 'Vertigo effect' },
                          ].map((m) => (
                            <button key={m.value}
                              onClick={() => setMotionPreset(m.value)}
                              data-testid={`motion-${m.value}`}
                              className={`p-2.5 rounded-xl border text-left transition-all ${
                                motionPreset === m.value
                                  ? 'bg-primary/10 border-primary text-white'
                                  : 'bg-zinc-900/50 border-zinc-800 text-zinc-400 hover:border-zinc-700'
                              }`}>
                              <p className="font-medium text-xs">{m.label}</p>
                              <p className="text-[10px] opacity-60">{m.desc}</p>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Platform */}
                      <div className="space-y-3">
                        <Label className="text-zinc-400">Platform Optimization</Label>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
                          {PLATFORM_OPTIONS.map((p) => {
                            const Icon = p.icon;
                            return (
                              <button
                                key={p.value}
                                onClick={() => setPlatform(p.value)}
                                className={`p-3 rounded-xl border text-left transition-all ${
                                  platform === p.value
                                    ? 'bg-primary/10 border-primary text-white'
                                    : 'bg-zinc-900/50 border-zinc-800 text-zinc-400 hover:border-zinc-700'
                                }`}
                              >
                                <Icon className="w-4 h-4 mb-1" />
                                <p className="font-medium text-xs">{p.label}</p>
                                <p className="text-[10px] opacity-60">{p.aspect}</p>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Duration */}
                      <div className="space-y-3">
                        <Label className="text-zinc-400">Length</Label>
                        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                          {DURATION_OPTIONS.map((d) => (
                            <button
                              key={d.value}
                              onClick={() => setDuration(d.value)}
                              className={`p-3 rounded-xl border text-center transition-all ${
                                duration === d.value
                                  ? 'bg-primary/10 border-primary text-white'
                                  : 'bg-zinc-900/50 border-zinc-800 text-zinc-400 hover:border-zinc-700'
                              }`}
                            >
                              <p className="font-medium text-sm">{d.label}</p>
                              <p className="text-xs opacity-60">{d.desc}</p>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Toggles */}
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {/* Captions */}
                        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Subtitles className="w-5 h-5 text-zinc-400" />
                              <div>
                                <p className="font-medium text-sm">Captions</p>
                                <p className="text-xs text-zinc-500">Auto-generated subtitles</p>
                              </div>
                            </div>
                            <Switch
                              checked={captions}
                              onCheckedChange={setCaptions}
                            />
                          </div>
                        </div>

                        {/* Voiceover */}
                        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Mic className="w-5 h-5 text-zinc-400" />
                              <div>
                                <p className="font-medium text-sm">Voiceover</p>
                                <p className="text-xs text-zinc-500">AI narration</p>
                              </div>
                            </div>
                            <Switch
                              checked={voiceover}
                              onCheckedChange={setVoiceover}
                            />
                          </div>
                        </div>

                        {/* Music */}
                        <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Music className="w-5 h-5 text-zinc-400" />
                              <div>
                                <p className="font-medium text-sm">Background Music</p>
                                <p className="text-xs text-zinc-500">Auto-selected track</p>
                              </div>
                            </div>
                            <Switch
                              checked={backgroundMusic}
                              onCheckedChange={setBackgroundMusic}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Voice Selector (if voiceover enabled) */}
                      {voiceover && (
                        <VoiceSelector
                          selectedVoice={voice}
                          onVoiceChange={setVoice}
                          voiceMode={voiceMode}
                          onVoiceModeChange={setVoiceMode}
                          disabled={generating}
                        />
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Inspiration Section */}
              {!generating && (
                <div className="space-y-6">
                  <div className="section-divider">
                    <span className="flex items-center gap-2">
                      <Lightbulb className="w-4 h-4" />
                      Get Inspired
                    </span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                    {INSPIRATION_EXAMPLES.map((example, index) => {
                      const Icon = example.icon;
                      return (
                        <button
                          key={index}
                          onClick={() => handleInspirationClick(example)}
                          className="group relative bg-zinc-900/60 border border-zinc-800 rounded-2xl p-4 text-left hover:border-zinc-700 hover:bg-zinc-800/60 transition-all hover:scale-[1.02] active:scale-[0.98]"
                        >
                          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${example.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform`}>
                            <Icon className="w-5 h-5 text-white" />
                          </div>
                          <p className="text-xs uppercase tracking-wider text-zinc-500 font-medium mb-1">
                            {example.category}
                          </p>
                          <p className="text-sm text-zinc-300 line-clamp-2 group-hover:text-white transition-colors">
                            {example.prompt.slice(0, 60)}...
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Latest Result */}
              {latestResult && !generating && (
                <div className="max-w-2xl mx-auto animate-fade-in-up mt-8">
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-2xl overflow-hidden">
                    {/* Player */}
                    <div className="aspect-video relative bg-black overflow-hidden">
                      {latestResult.video_url ? (
                        <VideoPlayerWithControls
                          src={`${API}/media/${latestResult.id}/video`}
                          poster={latestResult.thumbnail_url}
                          videoId={latestResult.id}
                          token={token}
                          userTier={user?.tier}
                          isFounder={user?.is_founder}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Play className="w-16 h-16 text-white/50" />
                        </div>
                      )}
                      <div className="absolute top-3 left-3">
                        <span className="status-badge status-ready">
                          <Check className="w-3 h-3" />
                          Ready
                        </span>
                      </div>
                    </div>
                    {/* Info + Actions */}
                    <div className="p-5 space-y-4">
                      <div>
                        <h3 className="text-base font-semibold text-white mb-1 line-clamp-1">
                          {latestResult.topic || latestResult.prompt || 'Generated Video'}
                        </h3>
                        <p className="text-sm text-zinc-500 line-clamp-2">
                          {latestResult.prompt || latestResult.topic}
                        </p>
                      </div>
                      {latestResult.script && (
                        <div className="bg-zinc-800/50 rounded-xl p-4">
                          <p className="text-[10px] text-zinc-500 uppercase tracking-wider font-medium mb-2">Script</p>
                          <p className="text-sm text-zinc-400 leading-relaxed line-clamp-4">{latestResult.script}</p>
                        </div>
                      )}
                      <div className="flex gap-2 pt-1">
                        <Button
                          size="sm"
                          onClick={() => setActiveTab('gallery')}
                          className="flex-1"
                        >
                          <Layers className="w-4 h-4 mr-2" />
                          View in Gallery
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setPrompt(latestResult.prompt || latestResult.topic || '');
                            setLatestResult(null);
                          }}
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Remix
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Upload Tab */}
            <TabsContent value="upload" className="animate-fade-in-up">
              <div className="max-w-2xl mx-auto">
                <div className="border-2 border-dashed border-zinc-700 rounded-3xl p-12 text-center hover:border-primary/50 transition-colors">
                  <input
                    type="file"
                    accept="video/*"
                    onChange={handleUpload}
                    className="hidden"
                    id="video-upload"
                  />
                  <label htmlFor="video-upload" className="cursor-pointer block">
                    <div className="w-20 h-20 mx-auto mb-6 bg-zinc-800 rounded-2xl flex items-center justify-center">
                      <Upload className="w-10 h-10 text-zinc-500" />
                    </div>
                    <h3 className="text-xl font-semibold mb-2">Upload Your Videos</h3>
                    <p className="text-zinc-500 mb-6">
                      Drag and drop or click to browse
                    </p>
                    <p className="text-xs text-zinc-600">
                      Supports MP4, MOV, WebM, AVI • Max 500MB
                    </p>
                  </label>
                </div>

                {/* Upload Use Cases */}
                <div className="grid grid-cols-3 gap-4 mt-8">
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-center">
                    <Film className="w-6 h-6 mx-auto mb-2 text-primary" />
                    <p className="text-sm font-medium">Enhance Quality</p>
                    <p className="text-xs text-zinc-500 mt-1">Upscale & improve</p>
                  </div>
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-center">
                    <Subtitles className="w-6 h-6 mx-auto mb-2 text-purple-400" />
                    <p className="text-sm font-medium">Add Captions</p>
                    <p className="text-xs text-zinc-500 mt-1">Auto-generate subtitles</p>
                  </div>
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4 text-center">
                    <Music className="w-6 h-6 mx-auto mb-2 text-blue-400" />
                    <p className="text-sm font-medium">Add Music</p>
                    <p className="text-xs text-zinc-500 mt-1">Background tracks</p>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Gallery Tab */}
            <TabsContent value="gallery" className="animate-fade-in-up">
              <MediaGallery
                items={[...jobs, ...videos].map(v => ({
                  ...v,
                  type: 'video',
                  source: v.source || 'generated',
                  title: v.topic || v.prompt?.slice(0, 50),
                  video_url: v.video_url ? `${API}/media/${v.id}/video` : null
                }))}
                token={token}
                onRefresh={() => { fetchVideos(); fetchJobs(); }}
                contentType="video"
                showFilters={true}
                emptyMessage="No videos yet"
                emptyAction={
                  <Button onClick={() => setActiveTab('create')} className="btn-oracle">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Create Your First Video
                  </Button>
                }
              />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
    </AppShell>
  );
};

export default VideosStudioPage;
